import { RichText } from "@payloadcms/richtext-lexical/react";
import { useTranslations } from "next-intl";
import type { BlogForClient } from "@/features/blog/shared/types";
import { Link } from "@/i18n/navigation";
import { Image } from "@/lib/client/components/Image";
import { AspectRatio, Badge, HStack, Text, VStack } from "@/ui";

interface BlogFeaturedProps {
  post: BlogForClient;
}

/**
 * BlogFeatured component - Hero featured post for blog listing
 * Server-rendered for above-fold performance
 */
export const BlogFeatured = ({ post }: BlogFeaturedProps) => {
  const t = useTranslations("blog");

  // Calculate read time (rough estimate: 200 words per minute)
  const wordCount = post.introduction?.root?.children
    ? JSON.stringify(post.introduction.root.children).split(" ").length
    : 0;
  const readTime = Math.max(1, Math.ceil(wordCount / 200));

  return (
    <Link
      href={`/blog/${post.slug}`}
      className="group block w-full cursor-pointer"
    >
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-5 lg:gap-12">
        {/* Image - 60% on desktop */}
        <div className="relative overflow-hidden rounded-lg lg:col-span-3">
          <AspectRatio ratio={4 / 3}>
            <Image
              src={post.headerImage.src}
              alt={post.headerImage.alt || post.title}
              fill
              priority
              sizes="(max-width: 1024px) 100vw, 60vw"
              className="h-full object-cover object-center transition-transform duration-500 ease-out group-hover:scale-[1.03]"
            />
          </AspectRatio>
        </div>

        {/* Content - 40% on desktop */}
        <VStack className="justify-center lg:col-span-2">
          <HStack className="mb-4 items-center gap-3">
            <span className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
              {t("featured_label")}
            </span>
            {post.categories?.[0] && (
              <Badge
                variant="outline"
                className="rounded-full border-primary/30 px-3 py-0.5 text-xs"
              >
                {post.categories[0]}
              </Badge>
            )}
          </HStack>

          <Text
            as="h2"
            className="font-serif text-2xl font-medium leading-tight tracking-tight sm:text-3xl"
          >
            {post.title}
          </Text>

          <Text className="mt-3 text-sm text-muted-foreground">
            {t("read_time", { minutes: readTime })}
          </Text>

          <div className="prose-sm mt-4 line-clamp-4 text-muted-foreground">
            <RichText
              className="prose-sm text-muted-foreground"
              data={post.introduction}
            />
          </div>
        </VStack>
      </div>
    </Link>
  );
};
